import type {
  Lead,
  LeadSource,
  LeadStatus,
} from '@up2stay/up2promo-api-client';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';

/** Sentinel for leads with no authenticated creator (public form). */
export const LEAD_FILTER_ANONYMOUS_CREATOR_ID = '__anonymous_creator__';

export function leadNormalizedCreatorFilterId(lead: Lead): string {
  const id = lead.createdByUserId?.trim();
  return id ? id : LEAD_FILTER_ANONYMOUS_CREATOR_ID;
}

/** Sentinel for leads with no assignee (Niemand). */
export const LEAD_FILTER_UNASSIGNED_ID = '__unassigned__';

/**
 * Explicit assignee id for filters. Unassigned → sentinel (not owner).
 * Legacy docs that still resolve assignee to owner on the API keep that id.
 */
export function leadNormalizedAssigneeFilterId(lead: Lead): string {
  const aid = lead.assigneeUserId?.trim();
  return aid ? aid : LEAD_FILTER_UNASSIGNED_ID;
}

/** Rolling windows from `lead.createdAt` (UTC). */
export type LeadsCreatedWithinPreset =
  | 'all'
  | 'last_day'
  | 'last_week'
  | 'last_month';

/** Structured list filters (OR within each multi-select dimension). */
export type LeadsListFilterCriteria = {
  statuses: LeadStatus[];
  sources: LeadSource[];
  assigneeUserIds: string[];
  tags: string[];
  eventIds: string[];
  createdWithin: LeadsCreatedWithinPreset;
};

export const EMPTY_LEADS_LIST_FILTERS: LeadsListFilterCriteria = {
  statuses: [],
  sources: [],
  assigneeUserIds: [],
  tags: [],
  eventIds: [],
  createdWithin: 'all',
};

const MS_DAY = 24 * 60 * 60 * 1000;

const CREATED_WITHIN_MS: Record<
  Exclude<LeadsCreatedWithinPreset, 'all'>,
  number
> = {
  last_day: MS_DAY,
  last_week: 7 * MS_DAY,
  last_month: 30 * MS_DAY,
};

/** Whether the lead falls into the chosen created-at window (inclusive lower bound). */
export function leadMatchesCreatedWithinPreset(
  lead: Lead,
  preset: LeadsCreatedWithinPreset,
  nowMs: number = Date.now(),
): boolean {
  if (preset === 'all') return true;
  const created = Date.parse(lead.createdAt);
  if (Number.isNaN(created)) return false;
  return created >= nowMs - CREATED_WITHIN_MS[preset];
}

export function leadMatchesSearchQuery(
  lead: Lead,
  searchQuery: string,
): boolean {
  const query = searchQuery.toLowerCase().trim();
  if (!query) return true;

  const name = formatLeadDisplayName(
    lead.firstName,
    lead.lastName,
  ).toLowerCase();
  const email = lead.email?.toLowerCase() || '';
  const companyName = lead.companyName?.toLowerCase() || '';
  const jobTitle = lead.jobTitle?.toLowerCase() || '';
  const phone = lead.phone?.toLowerCase() || '';
  const tagsMatch = (lead.tags ?? []).some((tag) =>
    String(tag ?? '')
      .toLowerCase()
      .includes(query),
  );

  return (
    name.includes(query) ||
    email.includes(query) ||
    companyName.includes(query) ||
    jobTitle.includes(query) ||
    phone.includes(query) ||
    tagsMatch
  );
}

/**
 * Apply structured filters + free-text search to a lead list.
 * Multi-select dimensions match when the lead hits any selected value (OR).
 */
export function applyLeadsListFilters(
  leads: Lead[],
  filters: LeadsListFilterCriteria,
  searchQuery: string = '',
  nowMs: number = Date.now(),
): Lead[] {
  let list = leads;

  if (filters.statuses.length > 0) {
    const allow = new Set<LeadStatus>(filters.statuses);
    list = list.filter((lead) => allow.has(lead.status));
  }

  if (filters.sources.length > 0) {
    const allow = new Set<LeadSource>(filters.sources);
    list = list.filter((lead) => allow.has(lead.source));
  }

  if (filters.assigneeUserIds.length > 0) {
    const allow = new Set(filters.assigneeUserIds);
    list = list.filter((lead) =>
      allow.has(leadNormalizedAssigneeFilterId(lead)),
    );
  }

  if (filters.tags.length > 0) {
    const allow = new Set(filters.tags.map((t) => t.toLowerCase()));
    list = list.filter((lead) =>
      (lead.tags ?? []).some((tag) => allow.has(String(tag).toLowerCase())),
    );
  }

  if (filters.eventIds.length > 0) {
    const allow = new Set(filters.eventIds);
    list = list.filter((lead) =>
      (lead.eventIds ?? []).some((id) => allow.has(id)),
    );
  }

  if (filters.createdWithin !== 'all') {
    list = list.filter((lead) =>
      leadMatchesCreatedWithinPreset(lead, filters.createdWithin, nowMs),
    );
  }

  if (!searchQuery.trim()) {
    return list;
  }

  return list.filter((lead) => leadMatchesSearchQuery(lead, searchQuery));
}

export function countActiveLeadsListFilters(
  filters: LeadsListFilterCriteria,
): number {
  return (
    filters.statuses.length +
    filters.sources.length +
    filters.assigneeUserIds.length +
    filters.tags.length +
    filters.eventIds.length +
    (filters.createdWithin !== 'all' ? 1 : 0)
  );
}

/**
 * Sheet / non-status facets (mobile Filter button badge).
 * Status stays in the chip row outside the sheet.
 */
export function countActiveLeadsSheetFilters(
  filters: LeadsListFilterCriteria,
  includeArchived = false,
): number {
  return (
    filters.sources.length +
    filters.assigneeUserIds.length +
    filters.tags.length +
    filters.eventIds.length +
    (filters.createdWithin !== 'all' ? 1 : 0) +
    (includeArchived ? 1 : 0)
  );
}

/**
 * Desktop “Mehr” overflow: tags / source / period only
 * (Event + Assignee + Status are already chip controls).
 */
export function countActiveLeadsOverflowFilters(
  filters: LeadsListFilterCriteria,
): number {
  return (
    filters.sources.length +
    filters.tags.length +
    (filters.createdWithin !== 'all' ? 1 : 0)
  );
}

/** Clear sheet/overflow dimensions; keep status (chip row). */
export function clearLeadsSheetFilters(
  filters: LeadsListFilterCriteria,
): LeadsListFilterCriteria {
  return {
    ...filters,
    sources: [],
    assigneeUserIds: [],
    tags: [],
    eventIds: [],
    createdWithin: 'all',
  };
}

/** Clear Mehr-only dimensions; keep status / events / assignee. */
export function clearLeadsOverflowFilters(
  filters: LeadsListFilterCriteria,
): LeadsListFilterCriteria {
  return {
    ...filters,
    sources: [],
    tags: [],
    createdWithin: 'all',
  };
}

/** Distinct tags from leads, sorted case-insensitively. */
export function collectDistinctLeadTags(
  leads: Lead[],
  locale: string = 'en',
  /** Case-insensitive exclusions (e.g. Event auto-tags when an Events filter exists). */
  excludeTags: readonly string[] = [],
): string[] {
  const excluded = new Set(
    excludeTags.map((t) => t.trim().toLowerCase()).filter(Boolean),
  );
  const seen = new Map<string, string>();
  for (const lead of leads) {
    for (const tag of lead.tags ?? []) {
      const trimmed = String(tag ?? '').trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (excluded.has(key)) continue;
      if (!seen.has(key)) seen.set(key, trimmed);
    }
  }
  return [...seen.values()].sort((a, b) =>
    a.localeCompare(b, locale, { sensitivity: 'base' }),
  );
}
